
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Hotel,
  MapPin,
  Mail,
  Phone,
  ArrowRight,
  ArrowUpRight,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Send,
  ChevronRight,
  Heart,
  ShieldCheck,
  Globe2,
} from "lucide-react";
import {
  FaFacebookF,
  FaInstagram,
  FaXTwitter,
  FaLinkedinIn,
  FaYoutube,
} from "react-icons/fa6";

const quickLinks = [
  { name: "Home", path: "/" },
  { name: "Explore Hotels", path: "/hotels" },
  { name: "Search Hotels", path: "/search-hotels" },
  { name: "About Us", path: "/about" },
  { name: "Contact Us", path: "/contact" },
];

const destinations = [
  { name: "Downtown Dubai", area: "Downtown Dubai" },
  { name: "Dubai Marina", area: "Dubai Marina" },
  { name: "Palm Jumeirah", area: "Palm Jumeirah" },
  { name: "Business Bay", area: "Business Bay" },
  { name: "Deira", area: "Deira" },
];

const socialLinks = [
  {
    name: "Facebook",
    icon: FaFacebookF,
    url: "https://www.facebook.com/SouthTravelsUAE/",
  },
  {
    name: "Instagram",
    icon: FaInstagram,
    url: "https://www.instagram.com/southtravels_dmcc/",
  },
  {
    name: "X (Twitter)",
    icon: FaXTwitter,
    url: "https://x.com/SouthTravels",
  },
  {
    name: "LinkedIn",
    icon: FaLinkedinIn,
    url: "https://www.linkedin.com/company/southtravelsuae/",
  },
  {
    name: "YouTube",
    icon: FaYoutube,
    url: "https://www.youtube.com/c/SouthTravelsdmcc",
  },

];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const handleSubscribe = (e) => {
    e.preventDefault();

    // Connect this form to your newsletter API.
    // Do not display a success message until the API
    // confirms that the subscription was saved.
    setMessage(
      "Newsletter subscriptions are not available yet."
    );
  };

  return (
    <footer className="relative overflow-hidden bg-[#053D2E] text-white">
      {/* DECORATIVE BACKGROUND */}
      <div
        aria-hidden="true"
        className="
          pointer-events-none absolute
          -right-28 -top-28
          h-[400px] w-[400px]
          rounded-full bg-[#168B43]/15
          blur-3xl
        "
      />

      <div
        aria-hidden="true"
        className="
          pointer-events-none absolute
          -bottom-40 -left-24
          h-[350px] w-[350px]
          rounded-full bg-[#1BA957]/10
          blur-3xl
        "
      />

      {/* SAME WIDTH AS HEADER */}
      <div
        className="
          relative z-10
          mx-auto w-full max-w-[1536px]
          px-4 sm:px-6 lg:px-8
        "
      >


        {/* MAIN FOOTER GRID */}
        <div
          className="
            grid grid-cols-1 gap-10
            py-12 sm:grid-cols-2
            lg:grid-cols-12
            lg:gap-8 lg:py-14
          "
        >
          {/* COLUMN 1: BRAND */}
          <div className="lg:col-span-4">
            <Link
              to="/"
              className="inline-flex items-center gap-3"
            >
              <span
                className="
                  flex h-12 w-12 items-center
                  justify-center rounded-[12px]
                  bg-[#168B43] text-white
                "
              >
                <Hotel size={25} strokeWidth={1.8} />
              </span>

              <div>
                <span
                  className="
                    block text-[20px] font-extrabold
                    leading-tight tracking-[-0.04em]
                    text-white
                  "
                >
                  Dubai<span className="text-[#7CE3A1]">
                    Budgetive
                  </span>
                </span>

                <span
                  className="
                    block text-[10px] font-bold
                    uppercase tracking-[0.3em]
                    text-white/65
                  "
                >
                  Hotels
                </span>
              </div>
            </Link>

            <p
              className="
                mt-5 max-w-[350px]
                text-[13px] leading-[1.9]
                text-white/65
              "
            >
              Discover comfortable stays across Dubai.
              Explore hotels in popular neighborhoods,
              compare accommodation options, and find
              the right place for your next trip.
            </p>

            {/* SOCIAL ICONS */}
            {/* SOCIAL MEDIA */}
            <div className="mt-7">
              <h4 className="mb-4 text-[12px] font-extrabold uppercase tracking-[0.16em] text-white">
                Follow Us
              </h4>

              <div className="flex flex-wrap items-center gap-3">
                {socialLinks.map((social) => {
                  const Icon = social.icon;

                  const hoverColors = {
                    Facebook: "hover:bg-[#1877F2]",
                    Instagram: "hover:bg-[#E4405F]",
                    "X (Twitter)": "hover:bg-black",
                    LinkedIn: "hover:bg-[#0A66C2]",
                    YouTube: "hover:bg-[#FF0033]",
                  };

                  return (
                    <a
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Follow us on ${social.name}`}
                      title={social.name}
                      className={`
            group flex h-11 w-11 items-center
            justify-center rounded-xl
            border border-white/15
            bg-white/10 text-white/80
            transition-all duration-300
            hover:-translate-y-1
            hover:border-transparent
            hover:text-white
            hover:shadow-lg
            ${hoverColors[social.name] || "hover:bg-[#168B43]"}
          `}
                    >
                      <Icon
                        size={18}
                        className="transition-transform duration-300 group-hover:scale-110"
                      />
                    </a>
                  );
                })}
              </div>
            </div>


          </div>

          {/* COLUMN 2: QUICK LINKS */}
          <div className="lg:col-span-2">
            <h3
              className="
                mb-6 text-[15px]
                font-extrabold text-white
              "
            >
              Quick Links
            </h3>

            <ul className="space-y-3.5">
              {quickLinks.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    className="
                      group inline-flex items-center
                      gap-2 text-[13px]
                      text-white/65
                      transition-colors
                      hover:text-[#7CE3A1]
                    "
                  >
                    <ChevronRight
                      size={14}
                      className="
                        text-[#51BA78]
                        transition-transform
                        group-hover:translate-x-1
                      "
                    />

                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* COLUMN 3: DESTINATIONS */}
          <div className="lg:col-span-3">
            <h3
              className="
                mb-6 text-[15px]
                font-extrabold text-white
              "
            >
              Popular Destinations
            </h3>

            <ul className="space-y-3.5">
              {destinations.map((destination) => (
                <li key={destination.name}>
                  <Link
                    to="/search-hotels"
                    state={{
                      destination: "Dubai",
                      area: destination.area,
                      location: destination.area,
                      guests: 2,
                      rooms: 1,
                    }}
                    className="
                      group inline-flex items-center
                      gap-2 text-[13px]
                      text-white/65
                      transition-colors
                      hover:text-[#7CE3A1]
                    "
                  >
                    <ChevronRight
                      size={14}
                      className="
                        text-[#51BA78]
                        transition-transform
                        group-hover:translate-x-1
                      "
                    />

                    {destination.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* COLUMN 4: CONTACT */}
          <div className="lg:col-span-3">
            <h3
              className="
                mb-6 text-[15px]
                font-extrabold text-white
              "
            >
              Get in Touch
            </h3>

            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <span
                  className="
                    flex h-9 w-9 shrink-0
                    items-center justify-center
                    rounded-[10px] bg-white/10
                    text-[#7CE3A1]
                  "
                >
                  <MapPin size={17} />
                </span>

                <div>
                  <p
                    className="
                      text-[12px] font-bold
                      text-white
                    "
                  >
                    Destination
                  </p>

                  <p
                    className="
                      mt-1 text-[12px]
                      leading-6 text-white/65
                    "
                  >
                    Office 302, 3rd Flr. HDS Tower Cluster F, Jumeirah Lakes Towers Dubai
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <span
                  className="
                    flex h-9 w-9 shrink-0
                    items-center justify-center
                    rounded-[10px] bg-white/10
                    text-[#7CE3A1]
                  "
                >
                  <Mail size={17} />
                </span>


                <div>
                  <p className="text-[12px] font-bold text-white">
                    Email Us
                  </p>

                  <a
                    href="mailto:info@southtravels.com"
                    className="
      mt-1 inline-flex items-center
      gap-1 text-[12px]
      text-white/65
      transition hover:text-[#7CE3A1]
    "
                  >
                    info@southtravels.com
                    <ArrowUpRight size={13} />
                  </a>
                </div>

              </div>

              <div className="flex items-start gap-3">
                <span
                  className="
                    flex h-9 w-9 shrink-0
                    items-center justify-center
                    rounded-[10px] bg-white/10
                    text-[#7CE3A1]
                  "
                >
                  <Phone size={17} />
                </span>

                <div>
                  <p
                    className="
                      text-[12px] font-bold
                      text-white
                    "
                  >
                    Need Assistance?
                  </p>

                  <Link
                    to="/contact"
                    className="
                      mt-1 inline-flex items-center
                      gap-1 text-[12px]
                      text-white/65
                      transition hover:text-[#7CE3A1]
                    "
                  >
                    Contact Our Team
                    <ArrowUpRight size={13} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TRUST STRIP */}
        <div
          className="
            flex flex-wrap items-center
            justify-center gap-x-9 gap-y-4
            border-t border-white/15
            py-6
          "
        >
          <div className="flex items-center gap-2">
            <ShieldCheck
              size={18}
              className="text-[#7CE3A1]"
            />
            <span className="text-[12px] text-white/70">
              Explore with Confidence
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Globe2
              size={18}
              className="text-[#7CE3A1]"
            />
            <span className="text-[12px] text-white/70">
              Discover Dubai Hotels
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Heart
              size={18}
              className="text-[#7CE3A1]"
            />
            <span className="text-[12px] text-white/70">
              Find Your Perfect Stay
            </span>
          </div>
        </div>

        {/* BOTTOM COPYRIGHT */}
        <div
          className="
            flex flex-col items-center
            justify-between gap-4
            border-t border-white/15
            py-6 text-center
            sm:flex-row sm:text-left
          "
        >
          <p className="text-[12px] text-white/55">
            © {new Date().getFullYear()} DubaiBudgetiveHotels.
            All rights reserved.
          </p>

          <div
            className="
              flex flex-wrap items-center
              justify-center gap-x-5 gap-y-2
            "
          >
            <Link
              to="/privacy-policy"
              className="
                text-[12px] text-white/55
                transition hover:text-[#7CE3A1]
              "
            >
              Privacy Policy
            </Link>

            <Link
              to="/terms-conditions"
              className="
                text-[12px] text-white/55
                transition hover:text-[#7CE3A1]
              "
            >
              Terms & Conditions
            </Link>

            <Link
              to="/contact"
              className="
                text-[12px] text-white/55
                transition hover:text-[#7CE3A1]
              "
            >
              Contact
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
